import {
  X,
  ArrowSquareOut,
  PencilSimple,
  TrashSimple,
  ArrowCounterClockwise,
  CheckCircle,
  Scales,
  Calendar,
  User,
  ClockCountdown,
  Warning,
  FileText,
  Gavel,
  LockKey,
} from '@phosphor-icons/react';
import { type Task, type TaskStatus, type TaskPriority, STATUS_LABELS, PRIORITY_LABELS, formatTimeEstimate } from '@/lib/tasksApi';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  task: Task;
  isSolo: boolean;
  canEdit: boolean;
  onClose: () => void;
  onEdit: () => void;
  onComplete: () => void;
  onReopen: () => void;
  onDelete: () => void;
  onNavigateToMatter?: (matterId: string) => void;
}

function priorityBadge(p: TaskPriority) {
  const map: Record<TaskPriority, string> = {
    urgent: 'text-red-600 dark:text-red-400',
    high:   'text-amber-600 dark:text-amber-400',
    normal: 'text-muted-foreground',
    low:    'text-blue-600 dark:text-blue-400',
  };
  return map[p];
}

function statusBadge(s: TaskStatus) {
  const map: Record<TaskStatus, string> = {
    completed:          'text-emerald-600 dark:text-emerald-400',
    in_progress:        'text-brand-600 dark:text-brand-400',
    blocked:            'text-red-600 dark:text-red-400',
    cancelled:          'text-zinc-400',
    awaiting_barrister: 'text-amber-600 dark:text-amber-400',
    awaiting_clerk:     'text-amber-600 dark:text-amber-400',
    hearing_followup:   'text-purple-600 dark:text-purple-400',
    filing_pending:     'text-purple-600 dark:text-purple-400',
    pending:            'text-muted-foreground',
  };
  return map[s];
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function dueLine(dueDate: string | null): { label: string; overdue: boolean } {
  if (!dueDate) return { label: '—', overdue: false };
  const d = new Date(dueDate);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(d); target.setHours(0, 0, 0, 0);
  const diff = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  const base = fmtDate(dueDate);
  if (diff < 0) return { label: `${base} (${Math.abs(diff)}d overdue)`, overdue: true };
  if (diff === 0) return { label: `${base} (today)`, overdue: false };
  if (diff === 1) return { label: `${base} (tomorrow)`, overdue: false };
  return { label: base, overdue: false };
}

// ─── Section label ────────────────────────────────────────────────────────────
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-4 py-2 sm:px-6 bg-muted/30 border-b border-border">
      <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/60">
        {children}
      </span>
    </div>
  );
}

// ─── Field row ────────────────────────────────────────────────────────────────
function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 px-4 py-2.5 sm:px-6 border-b border-border/50 last:border-0">
      <span className="w-24 shrink-0 text-xs text-muted-foreground/70 pt-px sm:w-32">{label}</span>
      <div className="min-w-0 flex-1 text-sm text-foreground">{children}</div>
    </div>
  );
}

export function TaskDetailPanel({ task, isSolo, canEdit, onClose, onEdit, onComplete, onReopen, onDelete, onNavigateToMatter }: Props) {
  const isCompleted = task.status === 'completed';
  const due = dueLine(task.dueDate);
  const hasHearing = Boolean(task.hearing);
  const hasMatter = Boolean(task.case);

  return (
    <>
      <ScrollLock />
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/20"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 z-50 flex h-full w-[480px] max-w-full flex-col bg-background shadow-2xl border-l border-border">

        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border px-5 py-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <FileText size={13} weight="duotone" />
            <span className="font-medium uppercase tracking-[0.06em]">Task Record</span>
            {task.isPrivate && (
              <span className="flex items-center gap-1 text-muted-foreground/60">
                <LockKey size={11} weight="fill" /> Private
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X size={15} weight="bold" />
          </button>
        </div>

        {/* Title block */}
        <div className="shrink-0 border-b border-border px-4 py-4 sm:px-6">
          <h2 className="text-[15px] font-semibold leading-snug text-foreground">{task.title}</h2>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <span className={`text-[10px] font-medium uppercase tracking-wider ${statusBadge(task.status)}`}>
              {STATUS_LABELS[task.status]}
            </span>
            <span className={`text-[10px] font-medium uppercase tracking-wider ${priorityBadge(task.priority)}`}>
              {PRIORITY_LABELS[task.priority]} priority
            </span>
            {due.overdue && !isCompleted && (
              <span className="flex items-center gap-1 text-[10px] font-semibold text-red-600 dark:text-red-400">
                <Warning size={10} weight="fill" /> Overdue
              </span>
            )}
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">

          {/* Task details */}
          <SectionLabel>Task Details</SectionLabel>
          <div>
            <FieldRow label="Status">
              <span className={`text-[11px] font-medium uppercase tracking-wide ${statusBadge(task.status)}`}>
                {STATUS_LABELS[task.status]}
              </span>
            </FieldRow>
            <FieldRow label="Priority">
              <span className={`text-[11px] font-medium uppercase tracking-wide ${priorityBadge(task.priority)}`}>
                {PRIORITY_LABELS[task.priority]}
              </span>
            </FieldRow>
            <FieldRow label="Due date">
              <span className={due.overdue && !isCompleted ? 'font-medium text-red-600 dark:text-red-400' : ''}>
                {due.label}
              </span>
            </FieldRow>
            {task.estimatedMinutes && (
              <FieldRow label="Time estimate">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <ClockCountdown size={13} weight="duotone" />
                  {formatTimeEstimate(task.estimatedMinutes)}
                </span>
              </FieldRow>
            )}
            <FieldRow label="Created">
              {fmtDateTime(task.createdAt)}
            </FieldRow>
            <FieldRow label="Created by">
              <span className="flex items-center gap-1.5">
                <User size={12} weight="duotone" className="text-muted-foreground" />
                {task.createdBy.firstName} {task.createdBy.lastName}
              </span>
            </FieldRow>
            {!isSolo && (
              <FieldRow label="Assigned to">
                {task.assignedTo ? (
                  <span className="flex items-center gap-1.5">
                    <User size={12} weight="duotone" className="text-muted-foreground" />
                    {task.assignedTo.firstName} {task.assignedTo.lastName}
                  </span>
                ) : (
                  <span className="text-muted-foreground/60">Unassigned</span>
                )}
              </FieldRow>
            )}
          </div>

          {/* Matter & context */}
          {(hasMatter || hasHearing) && (
            <>
              <SectionLabel>Matter & Context</SectionLabel>
              <div>
                {hasMatter && (
                  <FieldRow label="Matter">
                    {/* `items-center` with wrapping text floated the icons against the MIDDLE of
                        the wrapped block — the scales drifted between the two lines and the
                        external-link arrow ended up alone under the last word. The icons now
                        align to the first line, the name wraps as ordinary text, and the arrow
                        trails the last word inline. */}
                    {onNavigateToMatter ? (
                      <button
                        onClick={() => onNavigateToMatter(task.case!.id)}
                        className="flex w-full items-start gap-1.5 text-left text-brand-600 dark:text-brand-400 hover:underline"
                      >
                        <Scales size={12} weight="duotone" className="mt-[3px] shrink-0" />
                        <span className="min-w-0">
                          {task.case!.caseNumber} · {task.case!.caseName}
                          <ArrowSquareOut size={10} className="ml-1 inline shrink-0 align-baseline text-muted-foreground" />
                        </span>
                      </button>
                    ) : (
                      <span className="flex items-start gap-1.5 text-muted-foreground">
                        <Scales size={12} weight="duotone" className="mt-[3px] shrink-0" />
                        <span className="min-w-0">{task.case!.caseNumber} · {task.case!.caseName}</span>
                      </span>
                    )}
                  </FieldRow>
                )}
                {hasHearing && (
                  <FieldRow label="Hearing">
                    <span className="flex items-start gap-1.5 text-muted-foreground">
                      <Gavel size={12} weight="duotone" className="mt-[3px] shrink-0" />
                      {task.hearing!.hearingType} ·{' '}
                      {new Date(task.hearing!.hearingDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  </FieldRow>
                )}
              </div>
            </>
          )}

          {/* Description */}
          {task.description && (
            <>
              <SectionLabel>Description</SectionLabel>
              <div className="px-4 py-4 sm:px-6">
                <p className="text-sm leading-relaxed text-foreground/80 whitespace-pre-wrap">{task.description}</p>
              </div>
            </>
          )}

          {/* Completion record */}
          {isCompleted && (
            <>
              <SectionLabel>Completion Record</SectionLabel>
              <div>
                <FieldRow label="Completed by">
                  {task.completedBy ? (
                    <span className="flex items-center gap-1.5">
                      <CheckCircle size={12} weight="fill" className="text-emerald-600 dark:text-emerald-400" />
                      {task.completedBy.firstName} {task.completedBy.lastName}
                    </span>
                  ) : (
                    <span className="text-muted-foreground/60">—</span>
                  )}
                </FieldRow>
                <FieldRow label="Completed at">
                  <span className="flex items-center gap-1.5">
                    <Calendar size={12} weight="duotone" className="text-muted-foreground" />
                    {fmtDateTime(task.completedAt)}
                  </span>
                </FieldRow>
                {task.completionNote && (
                  <FieldRow label="Completion note">
                    <span className="text-sm leading-relaxed text-foreground/80 italic">
                      "{task.completionNote}"
                    </span>
                  </FieldRow>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer actions */}
        <div className="shrink-0 border-t border-border px-5 py-3">
          {!isCompleted ? (
            <div className="flex items-center gap-2">
              {canEdit && (
                <button
                  onClick={onComplete}
                  className="flex flex-1 items-center justify-center gap-2 rounded-md bg-foreground px-4 py-2 text-sm font-medium text-background transition hover:opacity-80"
                >
                  <CheckCircle size={15} weight="fill" />
                  Record completion
                </button>
              )}
              {canEdit && (
                <button
                  onClick={onEdit}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground"
                  title="Edit task"
                >
                  <PencilSimple size={14} />
                </button>
              )}
              {canEdit && (
                <button
                  onClick={onDelete}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-border text-muted-foreground transition hover:bg-red-50 dark:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 dark:hover:bg-red-900/20"
                  title="Delete task"
                >
                  <TrashSimple size={14} />
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex-1 text-xs text-muted-foreground">
                <CheckCircle size={12} weight="fill" className="inline mr-1 text-emerald-600 dark:text-emerald-400" />
                Completed {fmtDateTime(task.completedAt)}
              </div>
              {canEdit && (
                <button
                  onClick={onReopen}
                  className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
                >
                  <ArrowCounterClockwise size={13} />
                  Reopen
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
